<template>
  <div class="avatar-picker">
    <button
      v-for="key in AVATAR_KEYS"
      :key="key"
      type="button"
      class="avatar-option"
      :class="{ selected: modelValue === key }"
      :aria-label="avatarName(key)"
      :aria-pressed="modelValue === key"
      @click="$emit('update:modelValue', key)"
    >
      <AgentAvatar :avatar-key="key" :size="avatarSize" />
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

import AgentAvatar, {
  AVATAR_KEYS,
  AVATAR_NAMES,
  type AvatarKey,
} from "./AgentAvatar.vue";

export default defineComponent({
  name: "AgentAvatarPicker",
  components: { AgentAvatar },
  props: {
    modelValue: {
      type: String,
      default: "spark",
    },
    columns: {
      type: Number,
      default: 5,
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const avatarSize = computed(() => (props.columns >= 5 ? 52 : 90));
    const avatarName = (key: AvatarKey): string => AVATAR_NAMES[key] ?? key;
    return { AVATAR_KEYS, avatarName, avatarSize };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/_variables.scss" as *;
@use "../../styles/_themes.scss" as *;

.avatar-picker {
  display: grid;
  grid-template-columns: repeat(v-bind(columns), 1fr);
  gap: 8px;
}

.avatar-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid transparent;
  border-radius: $border-radius-m;
  padding: 8px 4px 6px;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  @include themed() {
    color: inherit;

    &:hover {
      background-color: t($grey-10);
    }

    &.selected {
      border-color: t($tertiary-50);
      background-color: t($tertiary-10);
    }
  }
}

.avatar-label {
  @extend %font-caption-regular;
  line-height: 1;

  @include themed() {
    color: t($grey-60);
  }

  .selected & {
    @include themed() {
      color: t($tertiary-100);
    }
  }
}
</style>
